import { ChangeDetectionStrategy, Component, HostBinding, HostListener, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CalendarDay, TimeRange, WeekCalendar } from 'src/app/models/diagnostic/WeekCalendar';

// Must divide 1440 and match (or be a multiple of) BE alarm.calendar.slot-minutes
const SLOT_MINUTES = 30;
const MINUTES_PER_DAY = 24 * 60;
// compact: hour labels every N hours, the slots are too short for more
const COMPACT_LABEL_HOURS = 6;
const WEEKDAYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

const toTime = (minutes: number) =>
  `${Math.floor(minutes / 60).toString().padStart(2, '0')}:${(minutes % 60).toString().padStart(2, '0')}`;
const toMinutes = (time: string) => Number(time.substring(0, 2)) * 60 + Number(time.substring(3, 5));

@Component({
  selector: 'app-week-grid',
  templateUrl: './week-grid.component.html',
  styleUrls: ['./week-grid.component.css'],
  // Only inputs and its own pointer events change the grid: skip the ~2000 per-cell bindings otherwise
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WeekGridComponent {
  @Input() set value(calendar: WeekCalendar | null | undefined) {
    this.selectedSlots = this.toSlots(calendar);
  }
  // false = read-only: no painting, no invert
  @Input() editMode: boolean = false;
  // Preview: short slots, no run labels, sparse hour labels
  @HostBinding('class.compact')
  @Input() compact: boolean = false;

  readonly weekdays = WEEKDAYS;
  readonly slots = Array.from({ length: MINUTES_PER_DAY / SLOT_MINUTES }, (_, i) => i);
  protected trackByIndex = (i: number) => i;
  protected selectedSlots: { [day: string]: Set<number> } = {};
  protected isSelecting: boolean = false;

  private isActivating: boolean = false;
  private dragAnchor: { day: number; slot: number } | null = null;
  private dragBase: { [day: string]: Set<number> } = {};

  constructor(private translate: TranslateService) {}

  /** The current selection as inclusion ranges, days without a selection left out. */
  toDays(): CalendarDay[] {
    const days: CalendarDay[] = [];
    for (const day of this.weekdays) {
      const slots = [...(this.selectedSlots[day] ?? [])].sort((a, b) => a - b);
      if (slots.length === 0) continue;
      const ranges: TimeRange[] = [];
      let start = slots[0];
      for (let i = 1; i <= slots.length; i++) {
        if (slots[i] === slots[i - 1] + 1) continue;
        ranges.push({ from: toTime(start * SLOT_MINUTES), to: toTime((slots[i - 1] + 1) * SLOT_MINUTES) });
        start = slots[i];
      }
      days.push({ day, ranges });
    }
    return days;
  }

  protected dayLabel(day: string): string {
    // 2024-01-01 is a Monday
    return new Intl.DateTimeFormat(this.translate.currentLang || undefined, { weekday: 'short' })
      .format(new Date(2024, 0, 1 + WEEKDAYS.indexOf(day)));
  }

  protected isHourStart(slot: number): boolean {
    return (slot * SLOT_MINUTES) % 60 === 0;
  }

  protected hourLabel(slot: number): string {
    if (slot === 0 || !this.isHourStart(slot)) return '';
    const minutes = slot * SLOT_MINUTES;
    if (this.compact && minutes % (COMPACT_LABEL_HOURS * 60) !== 0) return '';
    return toTime(minutes);
  }

  protected runLabel(slot: number, day: string): string | null {
    if (this.compact || !this.isSlotSelected(slot, day) || this.isSlotSelected(slot - 1, day)) return null;
    let end = slot + 1;
    while (this.isSlotSelected(end, day)) end++;
    return `${toTime(slot * SLOT_MINUTES)} – ${toTime(end * SLOT_MINUTES)}`;
  }

  protected isSlotSelected(slot: number, day: string): boolean {
    return this.selectedSlots[day]?.has(slot) ?? false;
  }

  protected startSelect(event: PointerEvent, slot: number, day: string) {
    if (!this.editMode || event.button !== 0) return;
    // Touch pointers are implicitly captured by the pressed cell, which suppresses pointerenter on the others
    const target = event.target as Element;
    if (target.hasPointerCapture?.(event.pointerId)) target.releasePointerCapture(event.pointerId);
    this.isActivating = !this.isSlotSelected(slot, day);
    this.isSelecting = true;
    this.dragAnchor = { day: this.weekdays.indexOf(day), slot };
    this.dragBase = this.copySlots(this.selectedSlots);
    this.onSlotDrag(slot, day);
  }

  protected invertDay(day: string) {
    if (!this.editMode) return;
    const current = this.selectedSlots[day] ?? new Set<number>();
    this.selectedSlots = { ...this.selectedSlots, [day]: new Set(this.slots.filter(slot => !current.has(slot))) };
  }

  // Excel-like: the rectangle between the pressed cell and the current one, applied over the selection
  // as it was when the drag started, so shrinking the rectangle gives the cells back
  protected onSlotDrag(slot: number, day: string): void {
    if (!this.isSelecting || !this.dragAnchor) return;
    const result = this.copySlots(this.dragBase);
    const dayIndex = this.weekdays.indexOf(day);
    const [fromDay, toDay] = [Math.min(this.dragAnchor.day, dayIndex), Math.max(this.dragAnchor.day, dayIndex)];
    const [fromSlot, toSlot] = [Math.min(this.dragAnchor.slot, slot), Math.max(this.dragAnchor.slot, slot)];
    for (let d = fromDay; d <= toDay; d++) {
      const daySlots = (result[this.weekdays[d]] = result[this.weekdays[d]] ?? new Set());
      for (let s = fromSlot; s <= toSlot; s++) {
        if (this.isActivating) daySlots.add(s);
        else daySlots.delete(s);
      }
    }
    this.selectedSlots = result;
  }

  @HostListener('document:pointerup')
  @HostListener('document:pointercancel')
  protected stopSelect() {
    if (!this.isSelecting) return;
    this.isSelecting = false;
    this.dragAnchor = null;
    this.dragBase = {};
  }

  private copySlots(slots: { [day: string]: Set<number> }): { [day: string]: Set<number> } {
    return Object.fromEntries(Object.entries(slots).map(([day, set]) => [day, new Set(set)]));
  }

  private toSlots(calendar: WeekCalendar | null | undefined): { [day: string]: Set<number> } {
    const result: { [day: string]: Set<number> } = {};
    for (const { day, ranges } of calendar?.days ?? []) {
      const daySlots = (result[day] = result[day] ?? new Set());
      for (const range of ranges) {
        const end = Math.ceil(toMinutes(range.to) / SLOT_MINUTES);
        for (let slot = Math.floor(toMinutes(range.from) / SLOT_MINUTES); slot < end; slot++) daySlots.add(slot);
      }
    }
    return result;
  }
}
